import { useEffect, useState, useMemo, useCallback } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  DataTable,
  DataTableColumnSettings,
  DataTableProvider,
  type TableColumnDef,
} from "../../components/data-table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Download,
  Eye,
  User,
  Mail,
  Phone,
  Building2,
  CreditCard,
  Receipt,
  FileText,
  Hash,
  Loader2,
  Plus,
  Search,
  MapPin,
  MessageSquare,
  CheckCircle2,
  DollarSign,
  Calendar,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  donationAPI,
  donationMasterAPI,
  donationSchemesAPI,
  donationTypeMasterAPI,
  fundingStatusAPI,
  statesAPI,
  districtsAPI,
  type DonationDetail,
  type DonationPayment,
  type FundingStatusData,
  type State,
  type District,
  type City,
} from "../../services/api";
import { CommonPagination } from "../../components/CommonPagination";
import { DonationListFilters } from "../../components/funding/DonationListFilters";
import {
  buildDonationListApiParams,
  donationListFilterInitialState,
  type DonationListFilterState,
  type DonationSchemeFilterOption,
  type DonationTypeFilterOption,
} from "../../components/funding/donationListFilterUtils";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { parseDate } from "../../lib/utils";
import logoImage from "../../../Jainboarding_User/assets/JBF logo.png";

// const CHART_COLORS = [
//   "hsl(var(--primary))",
//   "hsl(var(--accent))",
//   "hsl(var(--success))",
//   "hsl(var(--muted))",
// ];

// const DONATION_TYPE_COLORS: Record<string, string> = {
//   education: "hsl(var(--primary))",
//   standard: "hsl(var(--primary) / 0.78)",
//   infrastructure: "hsl(var(--primary) / 0.56)",
// };

function formatDate(iso?: string | null) {
  if (!iso || !iso.trim()) {
    return "—";
  }
  try {
    const d = parseDate(iso);
    if (!d) {
      return iso;
    }
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/** Prefer donation_date; fall back to created_at for older records */
function getDonationDate(donation: {
  donation_date?: string | null;
  created_at?: string | null;
}) {
  return donation.donation_date ?? donation.created_at ?? "";
}

const TABLE_CELL_TRUNCATE = "truncate whitespace-nowrap text-sm";

function TableEllipsisText({
  value,
  className,
}: {
  value: string | null | undefined;
  className?: string;
}) {
  const text = value?.trim() ?? "—";
  return (
    <span
      className={`block min-w-0 truncate ${className ?? ""}`.trim()}
      title={text}
    >
      {text}
    </span>
  );
}

function getPaymentStatus(donation: DonationDetail): string {
  const payments = donation.payments;
  if (!payments?.length) {
    return "—";
  }
  const latest = payments[0];
  return latest.status ?? "—";
}

function getDonationReceiptNo(donation: DonationDetail): string {
  const paymentReceipt = donation.payments?.[0]?.receipt_no;
  return donation.receipt_no ?? paymentReceipt ?? "-";
}

/** Build a safe PDF filename: {receipt_no}_JBF (slashes/invalid chars replaced) */
function getReceiptDownloadFileName(donation: DonationDetail): string {
  const receiptNo = getDonationReceiptNo(donation).trim() || "receipt";
  const safeName = receiptNo
    .replace(/[\\/:*?"<>|]+/g, "-")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  return `${safeName || "receipt"}_JBF`;
}

type AddDonationFormState = {
  name: string;
  email: string;
  phone: string;
  donation_type: string;
  amount: string;
  sum_of_rupees: string;
  message: string;
  organization: string;
  pan_number: string;
  aadhaar_number: string;
  mode: string;
  address_line1: string;
  address_line2: string;
  state: string;
  district: string;
  city: string;
  pincode: string;
  by_cheque_no: string;
  donation_date: string;
};

const addDonationInitialState: AddDonationFormState = {
  name: "",
  email: "",
  phone: "",
  donation_type: "",
  amount: "",
  sum_of_rupees: "",
  message: "",
  organization: "",
  pan_number: "",
  aadhaar_number: "",
  mode: "offline",
  address_line1: "",
  address_line2: "",
  state: "",
  district: "",
  city: "",
  pincode: "",
  by_cheque_no: "",
  donation_date: "",
};

const ONES = [
  "",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
  "Eleven",
  "Twelve",
  "Thirteen",
  "Fourteen",
  "Fifteen",
  "Sixteen",
  "Seventeen",
  "Eighteen",
  "Nineteen",
];
const TENS = [
  "",
  "",
  "Twenty",
  "Thirty",
  "Forty",
  "Fifty",
  "Sixty",
  "Seventy",
  "Eighty",
  "Ninety",
];

function twoDigitToWords(n: number): string {
  if (n < 20) {
    return ONES[n];
  }
  const ten = Math.floor(n / 10);
  const one = n % 10;
  return one ? `${TENS[ten]} ${ONES[one]}` : TENS[ten];
}

function threeDigitToWords(n: number): string {
  const hundred = Math.floor(n / 100);
  const rest = n % 100;
  if (hundred && rest) {
    return `${ONES[hundred]} Hundred ${twoDigitToWords(rest)}`;
  }
  if (hundred) {
    return `${ONES[hundred]} Hundred`;
  }
  return twoDigitToWords(rest);
}

/** Convert numeric amount to Indian rupees in words, e.g. 600 → "Six Hundred Rupees Only" */
function amountToRupeesInWords(amountStr: string): string {
  const raw = amountStr.trim();
  if (!raw || Number.isNaN(Number(raw))) {
    return "";
  }
  const num = Math.round(Number(raw) * 100) / 100;
  if (num < 0) {
    return "";
  }

  const rupees = Math.floor(num);
  const paise = Math.round((num - rupees) * 100);

  if (rupees === 0 && paise === 0) {
    return "Zero Rupees Only";
  }

  const parts: string[] = [];
  let remaining = rupees;

  const crore = Math.floor(remaining / 10000000);
  if (crore) {
    parts.push(`${threeDigitToWords(crore)} Crore`);
    remaining %= 10000000;
  }
  const lakh = Math.floor(remaining / 100000);
  if (lakh) {
    parts.push(`${twoDigitToWords(lakh)} Lakh`);
    remaining %= 100000;
  }
  const thousand = Math.floor(remaining / 1000);
  if (thousand) {
    parts.push(`${twoDigitToWords(thousand)} Thousand`);
    remaining %= 1000;
  }
  if (remaining) {
    parts.push(threeDigitToWords(remaining));
  }

  let words = rupees > 0 ? `${parts.join(" ")} Rupees` : "";
  if (paise > 0) {
    const paiseWords = twoDigitToWords(paise);
    words = words ? `${words} and ${paiseWords} Paise` : `${paiseWords} Paise`;
  }
  return `${words} Only`;
}

export default function Funding() {
  const [, setFundingStatus] = useState<FundingStatusData | null>(null);
  const [donationList, setDonationList] = useState<DonationDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [printingDonation, setPrintingDonation] =
    useState<DonationDetail | null>(null);
  const [selectedDonation, setSelectedDonation] =
    useState<DonationDetail | null>(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [donationFilters, setDonationFilters] =
    useState<DonationListFilterState>(donationListFilterInitialState);
  const [debouncedDonationSearch, setDebouncedDonationSearch] = useState("");
  const [debouncedDonationMessage, setDebouncedDonationMessage] = useState("");
  const [donationFiltersExpanded, setDonationFiltersExpanded] = useState(false);
  const [donationTypeOptions, setDonationTypeOptions] = useState<
    DonationTypeFilterOption[]
  >([]);
  const [paymentModeOptions, setPaymentModeOptions] = useState<
    DonationTypeFilterOption[]
  >([]);
  const [schemeOptions, setSchemeOptions] = useState<
    DonationSchemeFilterOption[]
  >([]);

  // Add Donation modal state
  const [isAddDonationOpen, setIsAddDonationOpen] = useState(false);
  const [addForm, setAddForm] = useState<AddDonationFormState>(
    addDonationInitialState
  );
  const [phoneSearch, setPhoneSearch] = useState("");
  const [phoneSearching, setPhoneSearching] = useState(false);
  const [phoneLookedUp, setPhoneLookedUp] = useState(false);
  const [foundDonorId, setFoundDonorId] = useState<string | number | null>(
    null
  );
  const [addSubmitting, setAddSubmitting] = useState(false);

  const [states, setStates] = useState<State[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [cities, setCities] = useState<City[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  useEffect(() => {
    const loadStates = async () => {
      setLoadingStates(true);
      try {
        const list = await statesAPI.list();
        setStates(list);
      } catch {
        toast.error("Failed to load states");
      } finally {
        setLoadingStates(false);
      }
    };
    loadStates();
  }, []);

  useEffect(() => {
    const loadDistricts = async () => {
      if (!addForm.state) {
        setDistricts([]);
        return;
      }
      setLoadingDistricts(true);
      try {
        const list = await statesAPI.getDistrictsByStateId(addForm.state);
        setDistricts(list);
      } catch {
        toast.error("Failed to load districts");
      } finally {
        setLoadingDistricts(false);
      }
    };
    loadDistricts();
  }, [addForm.state]);

  useEffect(() => {
    const loadCities = async () => {
      if (!addForm.district) {
        setCities([]);
        return;
      }
      setLoadingCities(true);
      try {
        const list = await districtsAPI.getCitiesByDistrictId(addForm.district);
        setCities(list);
      } catch {
        toast.error("Failed to load cities");
      } finally {
        setLoadingCities(false);
      }
    };
    loadCities();
  }, [addForm.district]);

  const activeDonationFilters = useMemo(
    () => ({
      ...donationFilters,
      search: debouncedDonationSearch,
      message: debouncedDonationMessage,
    }),
    [donationFilters, debouncedDonationSearch, debouncedDonationMessage]
  );

  const fetchDonationList = useCallback(async () => {
    setListLoading(true);
    setError(null);
    try {
      const params = buildDonationListApiParams(
        activeDonationFilters,
        currentPage,
        pageSize
      );
      const result = await donationAPI.getDonationDetails(params);
      setDonationList(result.donations);
      setTotalItems(result.count);
      setTotalPages(
        result.total_pages ?? Math.max(1, Math.ceil(result.count / pageSize))
      );

      const modes = [
        ...new Set(
          result.donations
            .map(row => row.payment_mode?.trim())
            .filter(Boolean) as string[]
        ),
      ].sort((a, b) => a.localeCompare(b));
      if (modes.length > 0) {
        setPaymentModeOptions(
          modes.map(mode => ({ value: mode, label: mode }))
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load donation list");
      setDonationList([]);
      setTotalItems(0);
      setTotalPages(0);
    } finally {
      setListLoading(false);
    }
  }, [activeDonationFilters, currentPage, pageSize]);

  useEffect(() => {
    let cancelled = false;

    async function fetchInitialData() {
      setLoading(true);
      try {
        const [status, types, schemesResult, modesResult] = await Promise.all([
          fundingStatusAPI.get(),
          donationTypeMasterAPI.list(),
          donationSchemesAPI.listPaginated({ page: 1, page_size: 1000 }),
          donationAPI.getDonationDetails({ page: 1, page_size: 500 }),
        ]);

        if (cancelled) {
          return;
        }

        setFundingStatus(status);
        setDonationTypeOptions(
          types
            .map(type => ({
              value: type.name?.trim() ?? String(type.id),
              label: type.name?.trim() ?? String(type.id),
            }))
            .filter(option => option.value)
            .sort((a, b) => a.label.localeCompare(b.label))
        );

        const schemes = schemesResult.data ?? schemesResult.results ?? [];
        setSchemeOptions(
          schemes
            .map(scheme => ({
              value: String(scheme.id),
              label: scheme.title?.trim() || `Campaign #${scheme.id}`,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))
        );

        const modes = [
          ...new Set(
            modesResult.donations
              .map(row => row.payment_mode?.trim())
              .filter(Boolean) as string[]
          ),
        ].sort((a, b) => a.localeCompare(b));
        setPaymentModeOptions(
          modes.map(mode => ({ value: mode, label: mode }))
        );
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "Failed to load funding data"
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void fetchInitialData();
    return () => {
      cancelled = true;
    };
  }, [setFundingStatus]);

  useEffect(() => {
    if (loading) {
      return;
    }
    void fetchDonationList();
  }, [fetchDonationList, loading]);

  // Refetch donations after adding
  const refetchDonations = useCallback(async () => {
    try {
      const status = await fundingStatusAPI.get();
      setFundingStatus(status);
      await fetchDonationList();
    } catch {
      // silent
    }
  }, [fetchDonationList, setFundingStatus]);

  const handleDownloadReceipt = async (donation: DonationDetail) => {
    const fileName = `${getReceiptDownloadFileName(donation)}.pdf`;
    setPrintingDonation(donation);
    toast.info("Preparing receipt PDF...");

    // Wait for React to paint the printable receipt DOM
    await new Promise<void>(resolve => {
      setTimeout(resolve, 400);
    });

    const element = document.getElementById("printable-receipt");
    if (!element) {
      toast.error("Receipt layout not ready. Please try again.");
      return;
    }

    const previousClassName = element.className;
    const previousStyle = element.getAttribute("style") ?? "";

    try {
      // Make receipt visible off-screen so html2canvas can capture it
      element.className = "flex flex-col bg-white font-sans";
      element.style.cssText = [
        "position: fixed",
        "left: -10000px",
        "top: 0",
        "width: 210mm",
        "height: 297mm",
        "display: flex",
        "flex-direction: column",
        "background: #ffffff",
        "z-index: -1",
        "overflow: hidden",
        "box-sizing: border-box",
      ].join(";");

      await new Promise<void>(resolve => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
      });

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
        logging: false,
        width: element.scrollWidth,
        height: element.scrollHeight,
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      const renderHeight = Math.min(imgHeight, pageHeight);

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, renderHeight);
      pdf.save(fileName);
      toast.success(`Downloaded as ${fileName}`);
    } catch (err) {
      console.error("Receipt PDF download failed:", err);
      toast.error("Failed to download receipt PDF. Please try again.");
    } finally {
      element.className = previousClassName;
      element.setAttribute("style", previousStyle);
    }
  };

  const handleViewDetails = async (donation: DonationDetail) => {
    setSelectedDonation(null);
    setIsDetailsModalOpen(true);
    setLoadingDetails(true);
    try {
      const details = await donationAPI.get(donation.id);
      setSelectedDonation(details);
    } catch (e) {
      console.error("Failed to fetch donation details:", e);
    } finally {
      setLoadingDetails(false);
    }
  };

  // ── Add Donation: phone search ──
  const handlePhoneSearch = useCallback(async () => {
    const phone = phoneSearch.trim();
    if (!phone || phone.length < 10) {
      toast.error("Please enter a valid 10-digit mobile number");
      return;
    }
    setPhoneSearching(true);
    try {
      const donor = await donationMasterAPI.searchByPhone(phone);
      if (donor) {
        setAddForm({
          name: donor.name ?? "",
          email: donor.email ?? "",
          phone: donor.phone ?? phone,
          donation_type: donor.donation_type ?? "",
          amount: String(donor.amount ?? ""),
          sum_of_rupees: amountToRupeesInWords(String(donor.amount ?? "")),
          message: donor.message ?? "",
          organization: donor.organization ?? "",
          pan_number: donor.pan_number ?? "",
          aadhaar_number: "",
          mode: "offline",
          address_line1: donor.address_line1 ?? "",
          address_line2: donor.address_line2 ?? "",
          state: String(donor.state_id ?? donor.state ?? ""),
          district: String(donor.district_id ?? donor.district ?? ""),
          city: String(donor.city_id ?? donor.city ?? ""),
          pincode: donor.pincode ?? "",
          by_cheque_no: donor.by_cheque_no ?? "",
          donation_date: "",
        });
        setFoundDonorId(donor.id);
        toast.success("Donor found! Form has been pre-filled.");
      } else {
        setAddForm({ ...addDonationInitialState, phone });
        setFoundDonorId(null);
        toast.info("No donor found. Please fill the details manually.");
      }
      setPhoneLookedUp(true);
    } catch {
      toast.error("Error searching for donor");
    } finally {
      setPhoneSearching(false);
    }
  }, [phoneSearch]);

  // ── Add Donation: submit (PATCH donation-master + POST donations/add) ──
  const handleAddDonationSubmit = useCallback(async () => {
    // Basic validations
    if (!addForm.name.trim()) {
      return toast.error("Name is required");
    }
    if (!addForm.email.trim()) {
      return toast.error("Email is required");
    }
    if (!addForm.phone.trim()) {
      return toast.error("Phone is required");
    }
    if (!addForm.donation_type) {
      return toast.error("Donation type is required");
    }
    if (!addForm.amount || parseFloat(addForm.amount) <= 0) {
      return toast.error("Valid donation amount is required");
    }
    if (!addForm.donation_date) {
      return toast.error("Donation date is required");
    }
    if (!addForm.pan_number.trim()) {
      return toast.error("PAN Number is required");
    }
    if (!addForm.address_line1.trim()) {
      return toast.error("Address Line 1 is required");
    }
    if (!addForm.state) {
      return toast.error("State is required");
    }
    if (!addForm.district) {
      return toast.error("District is required");
    }
    if (!addForm.city) {
      return toast.error("City is required");
    }
    if (!addForm.pincode.trim() || !/^[0-9]{6}$/.test(addForm.pincode)) {
      return toast.error("Valid 6-digit Pincode is required");
    }

    setAddSubmitting(true);
    try {
      const sumOfRupees =
        addForm.sum_of_rupees.trim() || amountToRupeesInWords(addForm.amount);

      // Common payload for donation-master (syncing/updating donor list)
      const masterPayload = {
        name: addForm.name.trim(),
        email: addForm.email.trim(),
        phone: addForm.phone.trim(),
        donation_type: addForm.donation_type,
        amount: addForm.amount,
        sum_of_rupees: sumOfRupees,
        message: addForm.message.trim(),
        organization: addForm.organization.trim(),
        pan_number: addForm.pan_number.trim(),
        mode: "offline",
        address_line1: addForm.address_line1.trim(),
        address_line2: addForm.address_line2.trim(),
        state: addForm.state,
        district: addForm.district,
        city: addForm.city,
        pincode: addForm.pincode.trim(),
        by_cheque_no: addForm.by_cheque_no.trim() || undefined,
      };

      // 1. PATCH if donor was found, else POST to create new donor
      if (foundDonorId) {
        await donationMasterAPI.update(foundDonorId, masterPayload);
      } else {
        await donationMasterAPI.create(masterPayload);
      }

      // 2. POST /jbf/donations/add/
      await donationAPI.addDonation({
        name: addForm.name.trim(),
        email: addForm.email.trim(),
        phone: addForm.phone.trim(),
        donation_type: addForm.donation_type,
        amount: addForm.amount,
        sum_of_rupees: sumOfRupees,
        mode: "offline",
        organization: addForm.organization.trim(),
        pan_number: addForm.pan_number.trim(),
        aadhaar_number: addForm.aadhaar_number.trim(),
        message: addForm.message.trim(),
        address_line1: addForm.address_line1.trim(),
        address_line2: addForm.address_line2.trim(),
        state: addForm.state,
        district: addForm.district,
        city: addForm.city,
        pincode: addForm.pincode.trim(),
        by_cheque_no: addForm.by_cheque_no.trim() || undefined,
        donation_date: addForm.donation_date,
      });

      toast.success("Donation added successfully!");
      setIsAddDonationOpen(false);
      setPhoneSearch("");
      setPhoneLookedUp(false);
      setAddForm(addDonationInitialState);
      setFoundDonorId(null);
      refetchDonations();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to add donation");
    } finally {
      setAddSubmitting(false);
    }
  }, [addForm, foundDonorId, refetchDonations]);

  // const pieChartData = useMemo(() => {
  //   const byType = fundingStatus?.by_type ?? [];
  //   if (!byType.length) {
  //     return [];
  //   }
  //   return byType.map((item, i) => ({
  //     name: item.donation_type,
  //     value: item.percentage,
  //     amount: item.amount,
  //     color:
  //       DONATION_TYPE_COLORS[item.donation_type.toLowerCase()] ??
  //       CHART_COLORS[i % CHART_COLORS.length],
  //   }));
  // }, [fundingStatus?.by_type]);

  // const monthlyTrendData = useMemo(() => {
  //   const map = new Map<number, { month: string; amount: number }>();
  //   donationList.forEach(d => {
  //     try {
  //       const date = new Date(d.created_at);
  //       const sortKey = date.getFullYear() * 12 + date.getMonth();
  //       const monthLabel = date.toLocaleString("en-IN", {
  //         month: "short",
  //         year: "numeric",
  //       });
  //       const existing = map.get(sortKey);
  //       if (existing) {
  //         existing.amount += parseFloat(d.amount || "0");
  //       } else {
  //         map.set(sortKey, {
  //           month: monthLabel,
  //           amount: parseFloat(d.amount || "0"),
  //         });
  //       }
  //     } catch {
  //     }
  //   });
  //   return Array.from(map.entries())
  //     .sort((a, b) => a[0] - b[0])
  //     .slice(-6)
  //     .map(([, v]) => ({ month: v.month, amount: v.amount }));
  // }, [donationList]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedDonationSearch(donationFilters.search);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [donationFilters.search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedDonationMessage(donationFilters.message);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [donationFilters.message]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedDonationSearch,
    debouncedDonationMessage,
    donationFilters.donation_type,
    donationFilters.status,
    donationFilters.payment_mode,
    donationFilters.scheme,
  ]);

  const paymentHistoryData = useMemo<DonationPayment[]>(() => {
    if (!selectedDonation) {
      return [];
    }
    if (selectedDonation.mode === "offline") {
      return [
        {
          id: -1,
          razorpay_order_id: "",
          razorpay_payment_id: null,
          razorpay_signature: null,
          amount: selectedDonation.amount || "0",
          currency: "INR",
          status: "—",
          created_at:
            getDonationDate(selectedDonation) || selectedDonation.created_at,
          payment_method: selectedDonation.payment_mode || "—",
          payment_method_details: null,
          receipt_no: selectedDonation.receipt_no ?? null,
        },
      ];
    }
    return selectedDonation.payments ?? [];
  }, [selectedDonation]);

  const paymentHistoryColumns = useMemo<TableColumnDef<DonationPayment>[]>(
    () => [
      {
        id: "receipt",
        label: "Receipt",
        width: 120,
        headerClassName:
          "h-8 text-[11px] font-bold uppercase tracking-wide text-zinc-500",
        renderCell: p => (
          <span className="font-medium text-zinc-800">
            {p.receipt_no ?? "—"}
          </span>
        ),
      },
      {
        id: "paymentId",
        label: "Payment ID",
        width: 140,
        headerClassName:
          "h-8 text-[11px] font-bold uppercase tracking-wide text-zinc-500",
        renderCell: p => (
          <span className="font-mono text-xs text-zinc-800">
            {p.razorpay_payment_id ?? <span className="text-zinc-500">—</span>}
          </span>
        ),
      },
      {
        id: "method",
        label: "Method",
        width: 100,
        headerClassName:
          "h-8 text-[11px] font-bold uppercase tracking-wide text-zinc-500",
        renderCell: p => (
          <span className="capitalize text-zinc-800">
            {p.payment_method || "—"}
          </span>
        ),
      },
      {
        id: "status",
        label: "Status",
        width: 100,
        headerClassName:
          "h-8 text-[11px] font-bold uppercase tracking-wide text-zinc-500",
        renderCell: p => <PaymentStatusPill status={p.status} />,
      },
      {
        id: "amount",
        label: "Amount",
        width: 120,
        headerClassName:
          "h-8 text-right text-[11px] font-bold uppercase tracking-wide text-zinc-500",
        cellClassName: "text-right text-sm font-bold text-zinc-900",
        renderCell: p =>
          `₹${parseFloat(p.amount || "0").toLocaleString("en-IN")}`,
      },
    ],
    []
  );

  const donationListColumns = useMemo<TableColumnDef<DonationDetail>[]>(
    () => [
      {
        id: "sr",
        label: "Sr. No",
        width: 72,
        alwaysVisible: true,
        renderCell: (_row, rowIndex) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
      },
      {
        id: "name",
        label: "Name",
        width: 160,
        cellClassName: TABLE_CELL_TRUNCATE,
        renderCell: row => (
          <TableEllipsisText value={row.name} className="font-medium" />
        ),
      },
      {
        id: "email",
        label: "Email",
        width: 220,
        cellClassName: TABLE_CELL_TRUNCATE,
        renderCell: row => <TableEllipsisText value={row.email} />,
      },
      {
        id: "phone",
        label: "Phone",
        width: 130,
        cellClassName: TABLE_CELL_TRUNCATE,
        renderCell: row => <TableEllipsisText value={row.phone} />,
      },
      {
        id: "amount",
        label: "Amount",
        width: 120,
        renderCell: row =>
          `₹${parseFloat(row.amount || "0").toLocaleString("en-IN")}`,
      },
      {
        id: "donationType",
        label: "Donation Type",
        width: 140,
        cellClassName: TABLE_CELL_TRUNCATE,
        renderCell: row => <TableEllipsisText value={row.donation_type} />,
      },
      {
        id: "institutionName",
        label: "Institution Name",
        width: 180,
        cellClassName: TABLE_CELL_TRUNCATE,
        renderCell: row => (
          <TableEllipsisText
            value={row.scheme_institution_name}
            className="font-medium text-zinc-800"
          />
        ),
      },
      {
        id: "campaignName",
        label: "Campaign Name",
        width: 200,
        cellClassName: TABLE_CELL_TRUNCATE,
        renderCell: row => (
          <TableEllipsisText
            value={row.scheme_title}
            className="font-medium text-zinc-800"
          />
        ),
      },
      {
        id: "paymentStatus",
        label: "Payment Status",
        width: 120,
        renderCell: row => {
          const status = getPaymentStatus(row);
          return (
            <Badge
              variant={
                status === "success"
                  ? "default"
                  : status === "created" || status === "pending"
                    ? "secondary"
                    : "outline"
              }
              className={status === "success" ? "bg-green-600 text-white" : ""}
            >
              {status}
            </Badge>
          );
        },
      },
      {
        id: "mode",
        label: "Mode",
        width: 100,
        cellClassName: TABLE_CELL_TRUNCATE,
        renderCell: row => (
          <TableEllipsisText
            value={row.mode}
            className="font-medium capitalize text-zinc-800"
          />
        ),
      },
      {
        id: "date",
        label: "Date",
        width: 120,
        renderCell: row => formatDate(getDonationDate(row)),
      },
      {
        id: "actions",
        label: "Action",
        width: 140,
        alwaysVisible: true,
        headerClassName: "text-center",
        cellClassName: "!max-w-none !overflow-visible text-right",
        renderCell: row => (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              className="h-8 w-8 p-0"
              title="View Details"
              onClick={e => {
                e.stopPropagation();
                handleViewDetails(row);
              }}
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5 bg-blue-100 hover:bg-accent/80"
              onClick={e => {
                e.stopPropagation();
                handleDownloadReceipt(row);
              }}
              title="Download PDF Receipt"
            >
              <Download className="h-4 w-4 text-blue-600" />
              <span className="text-xs">Receipt</span>
            </Button>
          </div>
        ),
      },
    ],
    [currentPage, pageSize]
  );

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-6">
        <p className="text-muted-foreground">Loading donation status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 p-6">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          {error}
        </div>
      </div>
    );
  }

  // const totalFunding = fundingStatus?.total_funding ?? 0;
  // const byType = fundingStatus?.by_type ?? [];

  return (
    <div className="p-6">
      {/* Printable Receipt Layout - Hidden by default, visible only during print */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 0 !important;
          }
          html, body {
            height: 280mm !important;
            width: 210mm !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body * {
            visibility: hidden;
          }
          #printable-receipt, #printable-receipt * {
            visibility: visible;
          }
          #printable-receipt {
            position: absolute;
            left: 0;
            top: 10;
            width: 212mm;
            height: 278mm;
            max-height: 280mm;
            background-color: white !important;
            margin: 0 !important;
            padding: 0 !important;
            border: 2px solid #555 !important;
            box-sizing: border-box !important;
            display: flex !important;
            flex-direction: column !important;
            overflow: hidden !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
          }
        }
      `}</style>

      {printingDonation && (
        <div
          id="printable-receipt"
          className="hidden flex-col bg-white font-sans print:flex"
        >
          {/* Header */}
          <div className="flex items-center justify-between bg-[#7A1E1E] px-10 py-8 text-white">
            <div>
              {/* <h1 className="text-2xl font-bold leading-tight" style={{ fontFamily: "'Times New Roman', Times, serif" }}>
                  Jain Boarding
                </h1>
                <h1 className="text-2xl font-bold leading-tight" style={{ fontFamily: "'Times New Roman', Times, serif" }}>
                  Federation
                </h1> */}
              <div className="mb-4 flex items-center gap-3 bg-white px-3 py-2">
                <img src={logoImage} alt="JBF Logo" className="h-14 w-auto" />
              </div>
              <p
                className="mt-1 text-sm"
                style={{ fontFamily: "'Times New Roman', Times, serif" }}
              >
                Section-8 Company | Not-for-Profit
              </p>
              <p
                className="text-sm"
                style={{ fontFamily: "'Times New Roman', Times, serif" }}
              >
                CIN .: U88900MH2025NPL440787
              </p>
            </div>

            <div
              className="text-left text-xs leading-relaxed"
              style={{ fontFamily: "'Times New Roman', Times, serif" }}
            >
              <p className="font-semibold">
                B-208 (South Wing), Malad Shopping Centre,
              </p>
              <p className="font-semibold">
                S.V. Road, Malad (West) Mumbai - 400 064
              </p>
              <p className="mt-1">✉ jainboardingfederation@gmail.com</p>
              <p>📞 8169074905</p>
              <p className="mt-1 font-bold">PAN : AAGCJ7502B</p>
              <p className="font-bold">12AREG NO.: AAGCJ7502BE20251</p>
              <p className="font-bold">80G NO.: AAGCJ7502BF20261</p>
            </div>
          </div>

          {/* Title */}
          <div className="border-b bg-[#E9DCCB] py-2 text-center">
            <h3 className="text-lg font-bold tracking-wide text-[#8B2C2C]">
              DONATION RECEIPT
            </h3>
          </div>

          <div className="flex-grow px-10 py-8">
            {/* Receipt Info — Receipt No. | Campaign (if any) | Date */}
            <div
              className={`mb-6 grid items-start gap-4 text-sm ${
                printingDonation.scheme_title?.trim()
                  ? "grid-cols-3"
                  : "grid-cols-2"
              }`}
            >
              <div className="min-w-0">
                <p className="text-gray-500">Receipt No.</p>
                <p className="font-semibold">
                  {getDonationReceiptNo(printingDonation)}
                </p>
              </div>

              {printingDonation.scheme_title?.trim() && (
                <div className="min-w-0 px-2 text-center">
                  <p className="text-gray-500">Campaign Name</p>
                  <p className="font-semibold leading-snug text-zinc-900">
                    {printingDonation.scheme_title.trim()}
                  </p>
                </div>
              )}

              <div className="min-w-0 text-right">
                <p className="text-gray-500">Date</p>
                <p className="font-semibold">
                  {formatDate(getDonationDate(printingDonation))}
                </p>
              </div>
            </div>

            {/* Donor Information */}
            <div className="mb-6">
              <h4 className="mb-3 border-b pb-1 text-sm font-bold">
                Donor Information
              </h4>

              <div className="grid grid-cols-3 gap-4 text-sm">
                {/* 1. Full Name */}
                {printingDonation.name?.trim() && (
                  <div>
                    <p className="text-xs text-gray-500">Name</p>
                    <p className="font-semibold">{printingDonation.name}</p>
                  </div>
                )}
                {/* 2. Contact */}
                {printingDonation.phone?.trim() && (
                  <div>
                    <p className="text-xs text-gray-500">Contact</p>
                    <p className="font-semibold">{printingDonation.phone}</p>
                  </div>
                )}
                {/* 3. Email */}
                {printingDonation.email?.trim() && (
                  <div>
                    <p className="text-xs text-gray-500">Email</p>
                    <p className="font-semibold">{printingDonation.email}</p>
                  </div>
                )}
                {/* 4. PAN Number */}
                {printingDonation.pan_number?.trim() && (
                  <div>
                    <p className="text-xs text-gray-500">PAN No.</p>
                    <p className="font-semibold">
                      {printingDonation.pan_number}
                    </p>
                  </div>
                )}
                {/* 5. By Cash / Cheque No. */}
                {printingDonation.by_cheque_no?.trim() && (
                  <div>
                    <p className="text-xs text-gray-500">By Cash/Cheque No.</p>
                    <p className="font-semibold">
                      {printingDonation.by_cheque_no}
                    </p>
                  </div>
                )}
                {/* 6. Donation Category */}
                {printingDonation.donation_type?.trim() && (
                  <div>
                    <p className="text-xs text-gray-500">Donation Category</p>
                    <p className="font-semibold">
                      {printingDonation.donation_type}
                    </p>
                  </div>
                )}
                {/* 7. Address */}
                {(printingDonation.address_line1?.trim() ??
                  printingDonation.city_name ??
                  printingDonation.state_name ??
                  printingDonation.pincode) && (
                  <div>
                    <p className="text-xs text-gray-500">Address</p>
                    <p className="text-[11px] font-semibold leading-tight">
                      {printingDonation.address_line1?.trim()}
                      {printingDonation.address_line2?.trim() &&
                        `, ${printingDonation.address_line2.trim()}`}
                      {(printingDonation.city_name ??
                        printingDonation.district_name ??
                        printingDonation.state_name) && (
                        <>
                          <br />
                          {[
                            printingDonation.city_name ?? printingDonation.city,
                            printingDonation.district_name ??
                              printingDonation.district,
                            printingDonation.state_name ??
                              printingDonation.state,
                          ]
                            .filter(Boolean)
                            .join(", ")}
                          {printingDonation.pincode?.trim()
                            ? ` - ${printingDonation.pincode}`
                            : ""}
                        </>
                      )}
                    </p>
                  </div>
                )}
                {printingDonation.organization?.trim() && (
                  <div>
                    <p className="text-xs text-gray-500">Organization</p>
                    <p className="font-semibold">
                      {printingDonation.organization}
                    </p>
                  </div>
                )}
                {printingDonation.message?.trim() && (
                  <div>
                    <p className="text-xs text-gray-500">Donation Remarks</p>
                    <p className="font-semibold">{printingDonation.message}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Donation Details */}
            <div className="mb-6">
              <h4 className="mb-3 border-b pb-1 text-sm font-bold">
                Donation Details
              </h4>

              <div className="mb-4">
                <p className="text-xs text-gray-500">Amount (₹)</p>
                <p className="text-2xl font-bold">
                  ₹{" "}
                  {parseFloat(printingDonation.amount ?? 0).toLocaleString(
                    "en-IN"
                  )}
                </p>
              </div>

              {(printingDonation.sum_of_rupees?.trim() ??
                printingDonation.amount) && (
                <div className="mb-2 text-sm">
                  <p className="text-xs text-gray-500">Sum of Rupees</p>
                  <p className="font-semibold italic leading-snug text-zinc-800">
                    {printingDonation.sum_of_rupees?.trim() ??
                      amountToRupeesInWords(
                        String(printingDonation.amount ?? "")
                      )}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Signature + Tax Note — pinned to bottom above thank-you footer */}
          <div className="mt-auto flex-shrink-0 px-10 pb-4 pt-6">
            <div className="mb-6 flex items-end justify-end">
              <div className="w-52 text-center">
                <div className="mb-1 border-t border-gray-600" />
                <p className="text-sm font-semibold">Authorized Signatory</p>
                <p className="text-xs text-gray-500">
                  Jain Boarding Federation
                </p>
              </div>
            </div>

            <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
              <span className="font-semibold">Note:</span> This donation is
              eligible for tax exemption under Section 80G of the Income Tax
              Act, 1961. Please retain this receipt for your tax filing
              purposes.
            </div>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 border-t bg-gray-100 py-4 text-center text-[11px] text-gray-600">
            <p className="font-medium">
              Thank you for your generous contribution!
            </p>
          </div>
        </div>
      )}

      {/* Donation Details Modal */}
      <Dialog open={isDetailsModalOpen} onOpenChange={setIsDetailsModalOpen}>
        <DialogContent className="max-w-3xl gap-0 overflow-hidden p-0 sm:rounded-xl [&>button]:right-5 [&>button]:top-5 [&>button]:z-10 [&>button]:text-zinc-500 [&>button]:opacity-100 [&>button]:hover:text-zinc-900">
          <DialogHeader className="border-b border-zinc-200 bg-white px-6 pb-4 pr-14 pt-5 text-left">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <DialogTitle className="flex items-center gap-3 text-lg font-semibold tracking-tight text-zinc-900">
                <span
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]"
                  aria-hidden
                >
                  <DollarSign
                    className="h-5 w-5 text-[#8B3232]"
                    strokeWidth={2.25}
                  />
                </span>
                Donation Details
              </DialogTitle>
              {selectedDonation && !loadingDetails && (
                <span className="inline-flex items-center gap-2 rounded-full border border-[#f0dede] bg-[#FDF7F7] px-3.5 py-1 text-sm font-semibold capitalize text-[#8B3232]">
                  <span
                    className="h-2 w-2 shrink-0 rounded-full bg-[#c53030]"
                    aria-hidden
                  />
                  {selectedDonation.mode}
                </span>
              )}
            </div>
          </DialogHeader>

          {loadingDetails ? (
            <div className="flex h-[400px] flex-col items-center justify-center gap-4 bg-white">
              <Loader2 className="h-12 w-12 animate-spin text-[#8B3232]" />
              <p className="animate-pulse font-medium text-muted-foreground">
                Fetching donation details...
              </p>
            </div>
          ) : (
            selectedDonation && (
              <>
                <div className="max-h-[min(80vh,730px)] overflow-y-auto bg-white">
                  {/* Transaction summary */}
                  <div className="border-b border-zinc-200/80 bg-[#FDF7F7] px-6 py-5 md:col-span-2">
                    <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between sm:gap-8">
                      <div className="min-w-0 space-y-1 sm:flex-1">
                        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#8B3232]">
                          Transaction amount
                        </p>
                        <p className="text-2xl font-semibold tracking-tight text-zinc-900 sm:text-3xl">
                          ₹
                          {parseFloat(
                            selectedDonation.amount || "0"
                          ).toLocaleString("en-IN")}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-end gap-x-10 gap-y-4 sm:shrink-0 sm:justify-end sm:gap-x-12 md:gap-x-16">
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            Date
                          </p>
                          <p className="text-sm font-semibold text-zinc-900">
                            {formatDate(getDonationDate(selectedDonation))}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            Purpose
                          </p>
                          <p className="text-sm font-semibold text-zinc-900">
                            {selectedDonation.donation_type || "—"}
                          </p>
                        </div>
                        <div className="min-w-0 max-w-[220px] sm:max-w-[280px]">
                          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            Campaign name
                          </p>
                          <p
                            className="truncate text-sm font-semibold text-zinc-900"
                            title={
                              selectedDonation.scheme_title?.trim() ?? undefined
                            }
                          >
                            {selectedDonation.scheme_title?.trim() ?? "—"}
                          </p>
                        </div>
                      </div>
                    </div>
                    {selectedDonation.scheme_institution_name?.trim() && (
                      <div className="mt-5 border-t border-zinc-200/70 pt-4">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          Institution name
                        </p>
                        <p
                          className="mt-1 truncate text-sm font-semibold text-zinc-900"
                          title={selectedDonation.scheme_institution_name.trim()}
                        >
                          {selectedDonation.scheme_institution_name.trim()}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-6 px-6 py-5 md:grid-cols-2">
                    {/* Donor information */}
                    <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
                      <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                          <User className="h-4 w-4 text-[#8B3232]" />
                        </span>
                        Donor information
                      </h3>
                      <hr className="mb-4 border-zinc-200" />
                      <div className="grid gap-5">
                        <DetailItem
                          icon={<User className="h-3.5 w-3.5" />}
                          label="Full name"
                          value={selectedDonation.name}
                        />
                        <DetailItem
                          icon={<Mail className="h-3.5 w-3.5" />}
                          label="Email address"
                          value={selectedDonation.email}
                        />
                        <DetailItem
                          icon={<Phone className="h-3.5 w-3.5" />}
                          label="Phone number"
                          value={selectedDonation.phone}
                        />
                        <DetailItem
                          icon={<Building2 className="h-3.5 w-3.5" />}
                          label="Organization"
                          value={selectedDonation.organization ?? "—"}
                        />
                        <DetailItem
                          icon={<FileText className="h-3.5 w-3.5" />}
                          label="PAN number"
                          value={selectedDonation.pan_number ?? "—"}
                        />
                      </div>
                    </div>

                    {/* Address */}
                    <div className="rounded-xl border border-zinc-200 bg-white p-5 shadow-sm">
                      <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                          <MapPin className="h-4 w-4 text-[#8B3232]" />
                        </span>
                        Address
                      </h3>
                      <hr className="mb-4 border-zinc-200" />
                      <div className="grid gap-5">
                        <DetailItem
                          icon={<MapPin className="h-3.5 w-3.5" />}
                          label="Address line 1"
                          value={selectedDonation.address_line1 ?? "N/A"}
                        />
                        <DetailItem
                          icon={<MapPin className="h-3.5 w-3.5" />}
                          label="Address line 2"
                          value={selectedDonation.address_line2 ?? "N/A"}
                        />
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                          <DetailItem
                            icon={<MapPin className="h-3.5 w-3.5" />}
                            label="State"
                            value={String(
                              selectedDonation.state_name ??
                                selectedDonation.state ??
                                "N/A"
                            )}
                          />
                          <DetailItem
                            icon={<MapPin className="h-3.5 w-3.5" />}
                            label="District"
                            value={String(
                              selectedDonation.district_name ??
                                selectedDonation.district ??
                                "N/A"
                            )}
                          />
                        </div>
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                          <DetailItem
                            icon={<MapPin className="h-3.5 w-3.5" />}
                            label="City"
                            value={String(
                              selectedDonation.city_name ??
                                selectedDonation.city ??
                                "N/A"
                            )}
                          />
                          <DetailItem
                            icon={<MapPin className="h-3.5 w-3.5" />}
                            label="Pincode"
                            value={selectedDonation.pincode ?? "N/A"}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Message from donor */}
                    <div className="md:col-span-2">
                      <h3 className="mb-3 flex items-center gap-2.5 text-base font-bold text-zinc-900">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                          <MessageSquare className="h-4 w-4 text-[#8B3232]" />
                        </span>
                        Message from donor
                      </h3>
                      <div className="border-y border-zinc-200 py-4 pl-4 pr-4">
                        <p className="border-l-[3px] border-[#8B3232] pl-4 text-sm font-medium leading-relaxed text-zinc-500">
                          &quot;
                          {selectedDonation.message ??
                            "No special message provided with this donation."}
                          &quot;
                        </p>
                      </div>
                    </div>

                    {/* Payment history */}
                    <DataTableProvider
                      tableId="super-admin-funding-payment-history"
                      columns={paymentHistoryColumns}
                    >
                      <div className="space-y-3 md:col-span-2">
                        <div className="flex items-center justify-between gap-2">
                          <h3 className="flex items-center gap-2.5 text-base font-bold text-zinc-900">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#FCECEC]">
                              <FileText className="h-4 w-4 text-[#8B3232]" />
                            </span>
                            Payment history
                          </h3>
                          <DataTableColumnSettings
                            columns={paymentHistoryColumns}
                          />
                        </div>
                        <div className="overflow-x-auto rounded-lg border border-zinc-200">
                          <DataTable
                            embedded
                            hideToolbar
                            tableId="super-admin-funding-payment-history"
                            columns={paymentHistoryColumns}
                            data={paymentHistoryData}
                            getRowKey={p => p.id}
                            emptyMessage="No payment records found."
                            headerRowClassName="border-b border-zinc-200 bg-zinc-50/90 hover:bg-zinc-50/90"
                            rowClassName={() => "border-zinc-100"}
                          />
                        </div>
                      </div>
                    </DataTableProvider>
                  </div>
                </div>

                <DialogFooter className="gap-3 border-t border-zinc-200 bg-white px-6 py-4 sm:justify-end">
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-zinc-700 hover:bg-zinc-100 hover:text-zinc-900"
                    onClick={() => setIsDetailsModalOpen(false)}
                  >
                    Close
                  </Button>
                  <Button
                    type="button"
                    className="gap-2 bg-[#8B3232] font-semibold text-white hover:bg-[#743030]"
                    onClick={() => handleDownloadReceipt(selectedDonation)}
                  >
                    <Download className="h-4 w-4" />
                    Download receipt
                  </Button>
                </DialogFooter>
              </>
            )
          )}
        </DialogContent>
      </Dialog>

      {/* ── Add Donation Modal ── */}
      <Dialog
        open={isAddDonationOpen}
        onOpenChange={open => {
          setIsAddDonationOpen(open);
          if (!open) {
            setPhoneSearch("");
            setPhoneLookedUp(false);
            setAddForm(addDonationInitialState);
            setFoundDonorId(null);
          }
        }}
      >
        <DialogContent className="flex max-h-[95vh] max-w-2xl flex-col overflow-hidden rounded-none border-none p-0 shadow-2xl transition-all [&>button]:right-6 [&>button]:top-6 [&>button]:text-white/80 [&>button]:hover:text-white [&>button_svg]:h-5 [&>button_svg]:w-5">
          <div className="shrink-0 bg-[#7A1E1E] px-6 py-6 text-white">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-white/20 p-2">
                <Plus className="h-6 w-6 text-white" />
              </div>
              <div>
                <DialogTitle className="text-xl font-bold tracking-tight">
                  Manual Donation Entry
                </DialogTitle>
                <p className="mt-0.5 text-xs font-medium uppercase tracking-wider text-white/70">
                  Record contribution from existing or new donor
                </p>
              </div>
            </div>
          </div>

          <div className="flex-1 space-y-8 overflow-y-auto bg-white px-6 py-8">
            <div>
              <Label
                htmlFor="add-phone-search"
                className="text-sm font-semibold text-gray-700"
              >
                Mobile Number <span className="text-red-500">*</span>
              </Label>
              <div className="mt-2 flex gap-3">
                <div className="relative flex-1">
                  <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    id="add-phone-search"
                    className="h-11 border-gray-200 pl-10 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                    value={phoneSearch}
                    onChange={e => {
                      const val = e.target.value.replace(/[^0-9]/g, "");
                      setPhoneSearch(val);
                      if (phoneLookedUp) {
                        setPhoneLookedUp(false);
                        setAddForm(addDonationInitialState);
                        setFoundDonorId(null);
                      }
                    }}
                    placeholder="Enter 10-digit mobile number"
                    maxLength={10}
                    disabled={phoneSearching}
                  />
                </div>
                <Button
                  onClick={handlePhoneSearch}
                  disabled={phoneSearching || phoneSearch.length < 10}
                  className="h-11 shrink-0 bg-primary px-6 text-white shadow-sm transition-all hover:bg-primary/90 active:scale-95"
                >
                  {phoneSearching ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4" />
                  )}
                  <span className="ml-2 font-semibold">Search Donor</span>
                </Button>
              </div>
              {!phoneLookedUp && !phoneSearching && (
                <p className="mt-2 flex items-center gap-1 text-xs text-gray-500">
                  <Hash className="h-3 w-3" />
                  Search by phone to auto-fill existing donor details
                </p>
              )}
            </div>

            {phoneLookedUp && (
              <div className="animate-in fade-in slide-in-from-top-4 mt-6 space-y-6 border-t pt-6 duration-300">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-bold text-gray-900">
                    Donor Details
                  </h3>
                  {foundDonorId && (
                    <Badge className="border-none bg-green-100 px-3 py-1 text-green-800 hover:bg-green-100">
                      Existing Donor
                    </Badge>
                  )}
                </div>

                <div className="grid gap-5">
                  {/* 1. Full Name */}
                  <div className="space-y-2">
                    <Label htmlFor="add-name" className="text-sm font-semibold">
                      Full Name *
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <Input
                        id="add-name"
                        className="border-gray-200 pl-10 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                        value={addForm.name}
                        onChange={e =>
                          setAddForm(prev => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder="Donor name"
                      />
                    </div>
                  </div>

                  {/* 2. Contact | 3. Email */}
                  <div className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label
                        htmlFor="add-phone"
                        className="text-sm font-semibold"
                      >
                        Contact Phone *
                      </Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <Input
                          id="add-phone"
                          className="border-gray-200 pl-10 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                          value={addForm.phone}
                          onChange={e =>
                            setAddForm(prev => ({
                              ...prev,
                              phone: e.target.value,
                            }))
                          }
                          placeholder="Phone number"
                          maxLength={10}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label
                        htmlFor="add-email"
                        className="text-sm font-semibold"
                      >
                        Email Address *
                      </Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                        <Input
                          id="add-email"
                          className="border-gray-200 pl-10 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                          type="email"
                          value={addForm.email}
                          onChange={e =>
                            setAddForm(prev => ({
                              ...prev,
                              email: e.target.value,
                            }))
                          }
                          placeholder="donor@example.com"
                        />
                      </div>
                    </div>
                  </div>

                  {/* 4. PAN Number */}
                  <div className="space-y-2">
                    <Label htmlFor="add-pan" className="text-sm font-semibold">
                      PAN Number *
                    </Label>
                    <div className="relative">
                      <CreditCard className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <Input
                        id="add-pan"
                        className="border-gray-200 pl-10 uppercase focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                        value={addForm.pan_number}
                        onChange={e =>
                          setAddForm(prev => ({
                            ...prev,
                            pan_number: e.target.value.toUpperCase(),
                          }))
                        }
                        placeholder="Valid 10-char PAN"
                        maxLength={10}
                      />
                    </div>
                  </div>

                  {/* 5. Payment Amount */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="add-amount"
                      className="text-sm font-semibold"
                    >
                      Payment Amount (₹) *
                    </Label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 font-medium text-gray-500">
                        ₹
                      </span>
                      <Input
                        id="add-amount"
                        className="border-gray-200 pl-8 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                        type="number"
                        min="0"
                        step="0.01"
                        value={addForm.amount}
                        onChange={e => {
                          const value = e.target.value;
                          setAddForm(prev => ({
                            ...prev,
                            amount: value,
                            sum_of_rupees: amountToRupeesInWords(value),
                          }));
                        }}
                        placeholder="0.00"
                      />
                    </div>
                  </div>

                  {/* 6. Sum of Rupees (auto from amount) */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="add-sum-of-rupees"
                      className="text-sm font-semibold"
                    >
                      Sum of Rupees
                    </Label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <Input
                        id="add-sum-of-rupees"
                        className="border-gray-200 bg-gray-50 pl-10 italic text-gray-700 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                        value={addForm.sum_of_rupees}
                        readOnly
                        placeholder="Amount in words (auto-filled)"
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      Auto-filled from payment amount in words
                    </p>
                  </div>

                  {/* Donation Date */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="add-donation-date"
                      className="text-sm font-semibold"
                    >
                      Donation Date *
                    </Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <Input
                        id="add-donation-date"
                        type="date"
                        className="border-gray-200 pl-10 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                        value={addForm.donation_date}
                        onChange={e =>
                          setAddForm(prev => ({
                            ...prev,
                            donation_date: e.target.value,
                          }))
                        }
                        required
                      />
                    </div>
                  </div>

                  {/* 7. By cash / Cheque No */}
                  <div className="space-y-2">
                    <Label
                      htmlFor="add-by-cheque-no"
                      className="text-sm font-semibold text-gray-700"
                    >
                      By Cash / Cheque No.
                    </Label>
                    <div className="relative">
                      <Receipt className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <Input
                        id="add-by-cheque-no"
                        className="border-gray-200 pl-10 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                        value={addForm.by_cheque_no}
                        onChange={e =>
                          setAddForm(prev => ({
                            ...prev,
                            by_cheque_no: e.target.value,
                          }))
                        }
                        placeholder="Enter cash receipt or cheque number"
                      />
                    </div>
                  </div>

                  {/* 8. Donation Category */}
                  <div className="space-y-2">
                    <Label htmlFor="add-type" className="text-sm font-semibold">
                      Donation Category *
                    </Label>
                    <Select
                      value={addForm.donation_type || undefined}
                      onValueChange={value =>
                        setAddForm(prev => ({
                          ...prev,
                          donation_type: value,
                        }))
                      }
                    >
                      <SelectTrigger
                        id="add-type"
                        className="border-gray-200 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                      >
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="education">Education</SelectItem>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="infrastructure">
                          Infrastructure
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="add-org" className="text-sm font-semibold">
                      Organization / Trust Name
                    </Label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <Input
                        id="add-org"
                        className="border-gray-200 pl-10 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                        value={addForm.organization}
                        onChange={e =>
                          setAddForm(prev => ({
                            ...prev,
                            organization: e.target.value,
                          }))
                        }
                        placeholder="Organization name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="add-message"
                      className="text-sm font-semibold"
                    >
                      Additional Remarks
                    </Label>
                    <div className="relative">
                      <FileText className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                      <Textarea
                        id="add-message"
                        className="min-h-[80px] border-gray-200 pl-10 focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                        value={addForm.message}
                        onChange={e =>
                          setAddForm(prev => ({
                            ...prev,
                            message: e.target.value,
                          }))
                        }
                        placeholder="Enter any additional donation details"
                        rows={3}
                      />
                    </div>
                  </div>

                  {/* 9. Address */}
                  <div className="mt-4 space-y-5 rounded-xl border border-gray-100 bg-gray-50/50 p-5">
                    <div className="flex items-center gap-2 text-[#7A1E1E]">
                      <MapPin className="h-5 w-5" />
                      <h3 className="text-sm font-bold uppercase tracking-wide">
                        Address Information
                      </h3>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label
                          htmlFor="add-address-line-1"
                          className="text-xs font-bold uppercase text-gray-500"
                        >
                          Address Line 1 *
                        </Label>
                        <Input
                          id="add-address-line-1"
                          className="border-gray-200 bg-white focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                          value={addForm.address_line1}
                          onChange={e =>
                            setAddForm(prev => ({
                              ...prev,
                              address_line1: e.target.value,
                            }))
                          }
                          placeholder="Street address, P.O. box, etc."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label
                          htmlFor="add-address-line-2"
                          className="text-xs font-bold uppercase text-gray-500"
                        >
                          Address Line 2
                        </Label>
                        <Input
                          id="add-address-line-2"
                          className="border-gray-200 bg-white focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                          value={addForm.address_line2}
                          onChange={e =>
                            setAddForm(prev => ({
                              ...prev,
                              address_line2: e.target.value,
                            }))
                          }
                          placeholder="Apartment, suite, unit, etc. (optional)"
                        />
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label
                            htmlFor="add-state"
                            className="text-xs font-bold uppercase text-gray-500"
                          >
                            State *
                          </Label>
                          <Select
                            value={String(addForm.state) || undefined}
                            onValueChange={value =>
                              setAddForm(prev => ({
                                ...prev,
                                state: value,
                                district: "",
                                city: "",
                              }))
                            }
                          >
                            <SelectTrigger
                              id="add-state"
                              className="border-gray-200 bg-white"
                            >
                              <SelectValue
                                placeholder={
                                  loadingStates ? "Loading..." : "Select State"
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {states.map(s => (
                                <SelectItem key={s.id} value={String(s.id)}>
                                  {s.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor="add-district"
                            className="text-xs font-bold uppercase text-gray-500"
                          >
                            District *
                          </Label>
                          <Select
                            value={String(addForm.district) || undefined}
                            disabled={!addForm.state || loadingDistricts}
                            onValueChange={value =>
                              setAddForm(prev => ({
                                ...prev,
                                district: value,
                                city: "",
                              }))
                            }
                          >
                            <SelectTrigger
                              id="add-district"
                              className="border-gray-200 bg-white"
                            >
                              <SelectValue
                                placeholder={
                                  loadingDistricts
                                    ? "Loading..."
                                    : "Select District"
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {districts.map(d => (
                                <SelectItem key={d.id} value={String(d.id)}>
                                  {d.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label
                            htmlFor="add-city"
                            className="text-xs font-bold uppercase text-gray-500"
                          >
                            City *
                          </Label>
                          <Select
                            value={String(addForm.city) || undefined}
                            disabled={!addForm.district || loadingCities}
                            onValueChange={value =>
                              setAddForm(prev => ({ ...prev, city: value }))
                            }
                          >
                            <SelectTrigger
                              id="add-city"
                              className="border-gray-200 bg-white"
                            >
                              <SelectValue
                                placeholder={
                                  loadingCities ? "Loading..." : "Select City"
                                }
                              />
                            </SelectTrigger>
                            <SelectContent>
                              {cities.map(c => (
                                <SelectItem key={c.id} value={String(c.id)}>
                                  {c.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label
                            htmlFor="add-pincode"
                            className="text-xs font-bold uppercase text-gray-500"
                          >
                            Pincode *
                          </Label>
                          <Input
                            id="add-pincode"
                            className="border-gray-200 bg-white focus:border-[#7A1E1E] focus:ring-[#7A1E1E]/20"
                            value={addForm.pincode}
                            onChange={e =>
                              setAddForm(prev => ({
                                ...prev,
                                pincode: e.target.value.replace(/[^0-9]/g, ""),
                              }))
                            }
                            placeholder="6-digit"
                            maxLength={6}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 border-t pb-2 pt-6">
                  <Button
                    variant="ghost"
                    className="h-12 flex-1 border border-gray-200 font-semibold text-gray-500"
                    onClick={() => setIsAddDonationOpen(false)}
                    disabled={addSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="h-12 flex-[2] bg-primary font-bold text-white shadow-sm shadow-[#7A1E1E]/20 transition-all hover:bg-primary/90 active:scale-[0.98]"
                    onClick={handleAddDonationSubmit}
                    disabled={addSubmitting}
                  >
                    {addSubmitting ? (
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    ) : (
                      <Plus className="mr-2 h-5 w-5" />
                    )}
                    Complete Donation
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <div className="mt-0 print:hidden">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Donation List
            </h1>
            <p className="text-muted-foreground">
              Monitor funding distribution and donation list
            </p>
          </div>
          <Button onClick={() => setIsAddDonationOpen(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Add Donation
          </Button>
        </div>

        {/* Summary Cards: Total + by donation type */}
        {/* <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Donation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">
                ₹{totalFunding.toLocaleString("en-IN")}
              </div>
            </CardContent>
          </Card>
          {byType.map(item => (
            <Card key={item.donation_type}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {item.donation_type}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">
                  ₹{Number(item.amount).toLocaleString("en-IN")}
                </div>
                <p className="text-sm text-muted-foreground">
                  {item.percentage}% of total
                </p>
              </CardContent>
            </Card>
          ))}
        </div> */}

        {/* Charts */}
        {/* <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Funding Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              {pieChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      labelLine
                      label={({ name, value }) =>
                        `${name} ${Number(value).toFixed(2)}%`
                      }
                      outerRadius={92}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieChartData.map(entry => (
                        <Cell key={`cell-${entry.name}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(
                        value: number,
                        _name: string,
                        props: { payload?: { amount?: number } }
                      ) => [
                        `${value}%`,
                        props?.payload?.amount !== null
                          ? `₹${Number(props?.payload?.amount ?? 0).toLocaleString("en-IN")}`
                          : "",
                      ]}
                    />
                    <Legend
                      iconType="circle"
                      formatter={value => (
                        <span className="capitalize text-foreground">
                          {value}
                        </span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                  No distribution data
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Monthly Funding Trend</CardTitle>
            </CardHeader>
            <CardContent>
              {monthlyTrendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip
                      formatter={(value: number) => [
                        `₹${Number(value).toLocaleString("en-IN")}`,
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="amount"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                  No trend data
                </div>
              )}
            </CardContent>
          </Card>
        </div> */}

        {/* Donation List Table */}
        <Card className="mt-6">
          <CardContent className="p-3 pb-4">
            <DonationListFilters
              filters={donationFilters}
              onFiltersChange={setDonationFilters}
              filtersExpanded={donationFiltersExpanded}
              onFiltersExpandedChange={setDonationFiltersExpanded}
              donationTypes={donationTypeOptions}
              paymentModes={paymentModeOptions}
              schemes={schemeOptions}
            />

            <DataTable
              title={`Donation List (${totalItems})`}
              tableId="super-admin-funding-donations"
              columns={donationListColumns}
              data={donationList}
              loading={listLoading}
              getRowKey={row => row.id}
              emptyMessage={
                totalItems === 0 && !listLoading
                  ? "No donations match your filters"
                  : "No donations yet"
              }
              rowClassName={row => {
                const status = getPaymentStatus(row);
                const isCreatedRow =
                  typeof status === "string" &&
                  status.toLowerCase() === "created";
                return isCreatedRow
                  ? "cursor-pointer bg-red-100 hover:bg-red-200/80 dark:bg-red-950/40 dark:hover:bg-red-950/50"
                  : "cursor-pointer hover:bg-muted";
              }}
            />

            {totalItems > 0 && (
              <div className="mt-4 border-t pt-4">
                <CommonPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  totalCount={totalItems}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={size => {
                    setPageSize(size);
                    setCurrentPage(1);
                  }}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function PaymentStatusPill({ status }: { status: string | null | undefined }) {
  const raw = (status ?? "—").trim();
  const s = raw.toLowerCase();

  if (s === "success") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold capitalize text-emerald-800">
        <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
        {raw}
      </span>
    );
  }

  if (s === "created" || s === "pending") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold capitalize text-zinc-600">
        <CheckCircle2
          className="h-3.5 w-3.5 shrink-0 text-zinc-500"
          aria-hidden
        />
        {raw}
      </span>
    );
  }

  return (
    <span className="inline-flex items-center rounded-full bg-zinc-100 px-2.5 py-0.5 text-xs font-semibold capitalize text-zinc-600">
      {raw || "—"}
    </span>
  );
}

function DetailItem({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-400 [&_svg]:text-zinc-400">
        {icon}
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
          {label}
        </p>
        <p className="text-sm font-semibold text-zinc-900">{value}</p>
      </div>
    </div>
  );
}
